import React, { useMemo, useCallback } from "react";
import { downloadCSV } from "../utils/ExpenseHelpers";

const ExpenseLedger = ({ 
  ledger = [], 
  user,
  userMap = {},
  search = "", 
  setSelectedTx, 
  setShowModal,
  onEdit,
  onDelete,
  canEditTransaction,
  canDeleteTransaction,
  projects = [] // Added to context to access structural attributes like project labels
}) => {
  
  // 1. MEMOIZE USER NAME RESOLUTION (Fixes the ESLint dependency warning)
  const resolveUserName = useCallback((uid) => {
    if (!uid) return "System";
    const key = String(uid);
    if (userMap && userMap[key]) return userMap[key];
    if (user && String(user.id) === key) return "You";
    return `User (${key.slice(0, 6)}...)`;
  }, [user, userMap]);

  // Project lookup dictionary to quickly locate mapped identifiers across the dataset
  const projectLookup = useMemo(() => {
    const lookupMap = {};
    if (Array.isArray(projects)) {
      projects.forEach((p) => {
        if (p.project_id) {
          lookupMap[String(p.project_id)] = {
            name: p.project_name || "Unknown",
            code: p.project_code ? `[${p.project_code}] ` : "",
            client: p.project_meta?.client_name ? `(${p.project_meta.client_name}) ` : ""
          };
        }
      });
    }
    return lookupMap;
  }, [projects]);

  // 2. MULTI-IDENTITY MATCHING SCHEME (Ensures financial aggregation captures all row formats)
  const groupedLedger = useMemo(() => {
    const map = {};
    
    // Extracted safe array of all valid structural identities for the logged-in session actor
    const userIdentities = new Set(
      [user?.id, user?.auth_id, user?.profile_db_id]
        .filter(Boolean)
        .map(String)
    );
    
    ledger.forEach((l) => {
      const groupId = l.transaction_id || `personal-${l.id}`;

      if (!map[groupId]) {
        map[groupId] = { 
          id: groupId, 
          isPersonal: !l.transaction_id, 
          items: [], 
          totalReceived: 0, 
          totalSpent: 0, 
          created_at: l.created_at 
        };
      }
      
      map[groupId].items.push(l);
      
      // Cross-reference row user_id against the identity Set instead of just user.id
      if (userIdentities.has(String(l.user_id))) {
        const amount = Number(l.amount) || 0;
        if (l.entry_type === "credit") {
          map[groupId].totalReceived += amount;
        }
        if (l.entry_type === "debit" && !l.note?.toLowerCase().includes("sent to")) {
          map[groupId].totalSpent += amount;
        }
      }
    });

    return Object.values(map).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [ledger, user?.id, user?.auth_id, user?.profile_db_id]);

  // 3. SECURE FILTERING (Safely includes resolveUserName and project details as stable dependencies)
  const filteredGroups = useMemo(() => {
    const s = (search || "").toLowerCase().trim();
    if (!s) return groupedLedger;

    return groupedLedger.filter(g => 
      g.items.some(i => {
        const itemNote = (i.note || "").toLowerCase();
        const itemAmount = String(i.amount || "");
        const userName = resolveUserName(i.user_id).toLowerCase();
        
        // Include project meta metadata filters to keep context visible across search actions
        const projData = projectLookup[String(i.project_id)] || { name: "", code: "", client: "" };
        const projectMatchStr = `${projData.name} ${projData.code} ${projData.client}`.toLowerCase();
        
        return (
          itemNote.includes(s) || 
          itemAmount.includes(s) || 
          userName.includes(s) || 
          projectMatchStr.includes(s)
        );
      })
    );
  }, [groupedLedger, search, resolveUserName, projectLookup]);

  // Unified Local Edit Prompt Wrapper targeting independent ledger items
  const triggerEditPrompt = (targetItem) => {
    const newAmount = prompt("Enter new amount:", targetItem.amount);
    if (newAmount === null) return; 
    if (isNaN(newAmount) || Number(newAmount) <= 0) {
      alert("Please enter a valid amount.");
      return;
    }

    const newNote = prompt("Enter new note:", targetItem.note || "");
    if (newNote === null) return; 

    if (onEdit) {
      // Passes context identifiers clean up through down to storage handlers
      onEdit(targetItem, Number(newAmount), newNote.trim(), targetItem.project_id);
    }
  };

  return (
    <div style={styles.container}>
      {filteredGroups.map((group) => {
        const spent = group.totalSpent;
        const received = group.totalReceived;
        const remaining = received - spent;
        const percent = received > 0 ? Math.min((spent / received) * 100, 100) : 100;

        return (
          <div key={`group-${group.id}`} style={styles.groupCard}>
            
            {/* Header Area */}
            <div style={styles.headerArea}>
              <div>
                <span style={styles.groupBadge}>
                  {group.isPersonal ? "Personal Ledger" : `Shared Request Group: ${group.id?.slice(0, 8)}`}
                </span>
                <span style={styles.groupDate}>
                  Opened: {group.created_at ? new Date(group.created_at).toLocaleDateString() : "N/A"}
                </span>
              </div>
              
              <div style={{ display: "flex", gap: "8px" }}>
                {!group.isPersonal && (
                  <button
                    onClick={() => {
                      const transferEntry = group.items.find((i) => i.entry_type === "credit");
                      if (setSelectedTx) {
                        setSelectedTx({
                          id: group.id,
                          from_user_id: transferEntry ? transferEntry.user_id : user?.id
                        });
                      }
                      if (setShowModal) setShowModal(true);
                    }}
                    style={styles.addExpenseBtn}
                  >
                    ➕ Add Expenditure
                  </button>
                )}

                <button
                  onClick={() => {
                    const csvData = group.items.map((item) => {
                      const proj = projectLookup[String(item.project_id)] || { name: "No Project", code: "" };
                      return {
                        Date: new Date(item.created_at).toLocaleDateString(),
                        Project: `${proj.code}${proj.name}`,
                        User: resolveUserName(item.user_id),
                        Type: item.entry_type?.toUpperCase(),
                        Amount: item.amount,
                        Note: item.note || ""
                      };
                    });
                    downloadCSV(csvData, `Group_${group.id?.slice(0, 8)}.csv`);
                  }}
                  style={styles.csvBtn}
                >
                  📥 CSV
                </button>
              </div>
            </div>

            {/* --- ITEMIZED SUB-ROWS EXPLOSION --- */}
            <div style={styles.itemsList}>
              {group.items.map((item) => {
                const isItemEditable = canEditTransaction?.(item, user?.id);
                const isItemDeletable = canDeleteTransaction?.(item, user?.id);
                const isDebit = item.entry_type === "debit";
                const projInfo = projectLookup[String(item.project_id)];

                return (
                  <div key={`item-${item.id}-${item.created_at}`} style={styles.itemRow}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: "12px", fontWeight: "600", color: isDebit ? "#ef4444" : "#10b981", marginRight: "8px" }}>
                        [{item.entry_type?.toUpperCase()}]
                      </span>
                      <strong style={{ fontSize: "13px", color: "#1f2937" }}>
                        {item.note || "Uncategorized Item Entry"}
                      </strong>
                      
                      {/* Attached Project Badges */}
                      {projInfo && (
                        <span style={styles.projectInlineTag}>
                          📁 {projInfo.client}{projInfo.code}{projInfo.name}
                        </span>
                      )}
                      
                      <span style={{ fontSize: "11px", color: "#9ca3af", marginLeft: "10px" }}>
                        by {resolveUserName(item.user_id)}
                      </span>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <span style={{ fontWeight: "bold", fontSize: "14px", color: isDebit ? "#ef4444" : "#10b981" }}>
                        {isDebit ? "-" : "+"} ₹{Number(item.amount).toLocaleString()}
                      </span>
                      
                      {/* Sub-row Level Actions */}
                      {(isItemEditable || isItemDeletable) && (
                        <div style={{ display: "flex", gap: "4px" }}>
                          {isItemEditable && (
                            <button 
                              onClick={() => triggerEditPrompt(item)}
                              style={styles.editBtn}
                            >
                              ✏️ Edit
                            </button>
                          )}
                          {isItemDeletable && (
                            <button 
                              onClick={() => {
                                if (window.confirm("Delete this specific row record?")) {
                                  if (onDelete) onDelete(item);
                                }
                              }}
                              style={styles.deleteBtn}
                            >
                              🗑️ Delete
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Group Progress Summary Footer */}
            {!group.isPersonal && (
              <>
                <div style={styles.progressBarBg}>
                  <div style={{ width: `${percent}%`, background: percent > 90 ? "#ef4444" : "#10b981", height: "100%", transition: "width 0.3s ease" }} />
                </div>
                <div style={styles.progressFooterDetails}>
                  <span>Spent Allocation: <strong>₹{spent.toLocaleString()}</strong></span>
                  <span>Remaining Fund: <strong style={{ color: remaining < 0 ? "#ef4444" : "#059669" }}>₹{remaining.toLocaleString()}</strong></span>
                  <span>Received limit: <strong>₹{received.toLocaleString()}</strong></span>
                </div>
              </>
            )}

          </div>
        );
      })}
    </div>
  );
};

const styles = {
  container: { display: "flex", flexDirection: "column", gap: "20px" },
  groupCard: { border: "1px solid #e5e7eb", padding: 18, borderRadius: 12, background: "#fff", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" },
  headerArea: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, borderBottom: "1px solid #f3f4f6", paddingBottom: "10px" },
  groupBadge: { fontSize: "11px", color: "#6b7280", display: "block", textTransform: "uppercase", fontWeight: "bold" },
  groupDate: { fontSize: "13px", color: "#4b5563" },
  addExpenseBtn: { background: "#6366f1", color: "#fff", padding: "6px 12px", border: "none", borderRadius: 6, cursor: "pointer", fontSize: "12px", fontWeight: "600" },
  csvBtn: { fontSize: "12px", cursor: "pointer", padding: "5px 10px", background: "#fff", border: "1px solid #d1d5db", borderRadius: 6, color: "#4b5563" },
  itemsList: { display: "flex", flexDirection: "column", gap: "8px", margin: "12px 0" },
  itemRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f9fafb", borderRadius: 8, border: "1px solid #f3f4f6" },
  projectInlineTag: { marginLeft: "8px", fontSize: "11px", background: "#e0e7ff", color: "#4338ca", padding: "2px 6px", borderRadius: "4px", display: "inline-block" },
  editBtn: { padding: "4px 8px", background: "#f59e0b", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: "11px" },
  deleteBtn: { padding: "4px 8px", background: "#ef4444", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: "11px" },
  progressBarBg: { background: "#f3f4f6", height: 6, borderRadius: 3, overflow: "hidden", marginBottom: 10, marginTop: 15 },
  progressFooterDetails: { display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#4b5563" }
};

export default ExpenseLedger;